/** Статусы занятия с точки зрения клиента (информирование, не запись) */

function optionTextLooksNo(text) {
  return /не приду|не смогу|не буду/i.test(String(text || ""));
}

function optionTextLooksYes(text) {
  const t = String(text || "").trim();
  if (!t || optionTextLooksNo(t)) return false;
  return /приду|буду/i.test(t);
}

export function optionForecast(pollRow, optionId) {
  if (!optionId) return null;
  const opts = pollRow?.poll?.options || pollRow?.options || [];
  return opts.find((o) => o.id === optionId)?.forecast || null;
}

export function surveyPollType(row) {
  return row?.survey_type || row?.poll_type || row?.poll?.poll_type || "attendance";
}

export function isAttendancePoll(row) {
  const type = surveyPollType(row);
  return !type || type === "attendance";
}

export function pollNeedsResponse(pollRow) {
  if (!pollRow) return false;
  if (pollRow.needs_response === false) return false;
  if (pollRow.needs_response === true) return true;
  if (pollRow.responded_at) return false;
  if (pollRow.poll?.closes_at && new Date(pollRow.poll.closes_at) < new Date()) {
    return false;
  }
  return true;
}

export function isPollOpen(pollRow) {
  if (!pollRow) return false;
  if (
    !pollRow.poll &&
    !pollRow.options?.length &&
    pollRow.needs_response !== true
  ) {
    return false;
  }
  if (pollRow.poll?.closes_at && new Date(pollRow.poll.closes_at) < new Date()) {
    return false;
  }
  if (pollRow.poll?.status === "closed") return false;
  if (pollRow.poll?.is_active === false) return false;
  return true;
}

export function pollResponseSummary(pollRow) {
  if (!pollRow?.responded_at || !pollRow.answers?.option_id) return null;
  const forecast = optionForecast(pollRow, pollRow.answers.option_id);
  if (!forecast) return null;
  return {
    forecast,
    reason: pollRow.answers.reason || null,
    updated_at: pollRow.responded_at,
  };
}

export function sessionRsvpSummary(session) {
  const rsvp = session?.client_rsvp;
  if (!rsvp?.forecast) return null;
  return {
    forecast: rsvp.forecast,
    reason: rsvp.reason || null,
    updated_at: rsvp.updated_at || null,
  };
}

function summaryTimestamp(summary) {
  if (!summary?.updated_at) return 0;
  const ts = new Date(summary.updated_at).getTime();
  return Number.isFinite(ts) ? ts : 0;
}

export function currentRsvpSummary(session, pollRow) {
  const poll = pollResponseSummary(pollRow);
  const direct = sessionRsvpSummary(session);
  if (poll && direct) {
    return summaryTimestamp(direct) >= summaryTimestamp(poll) ? direct : poll;
  }
  return poll || direct;
}

export function resolvedRsvpForecast(session, pollRow) {
  return currentRsvpSummary(session, pollRow)?.forecast || null;
}

export function pollForSession(pollMap, appointment) {
  if (!pollMap || !appointment) return null;
  const sid = appointment.session_id || appointment.id;
  if (sid && pollMap[sid]) return pollMap[sid];
  if (appointment.start_datetime) {
    const timeKey = `t:${new Date(appointment.start_datetime).toISOString()}`;
    if (pollMap[timeKey]) return pollMap[timeKey];
  }
  return null;
}

export function pollBySessionKey(polls) {
  const map = {};
  for (const p of polls || []) {
    const sid = p.session?.id || p.session_id;
    if (sid) map[sid] = p;
    const raw = p.session?.start_datetime;
    if (raw) {
      map[`t:${new Date(raw).toISOString()}`] = p;
    }
  }
  return map;
}

export function getSessionDisplayStatus(appointment, pollRow) {
  if (appointment.session_kind === "event") {
    if (appointment.event_kind === "competition") {
      if (appointment.competition_registration_status === "approved") {
        return { kind: "competition", label: "Вы участвуете", tone: "success" };
      }
      if (appointment.competition_registration_status === "pending") {
        return { kind: "competition_pending", label: "Заявка подана", tone: "poll" };
      }
      return { kind: "competition_event", label: "Соревнование", tone: "neutral" };
    }
    return { kind: "club_event", label: "Событие", tone: "neutral" };
  }

  const start = new Date(appointment.start_datetime);
  const past = start < new Date();

  if (appointment.status === "cancelled") {
    return { kind: "cancelled", label: "Отменено", tone: "warn" };
  }

  if (past) {
    if (appointment.status === "completed") {
      return { kind: "attended", label: "Был", tone: "success" };
    }
    return { kind: "missed", label: "Не был", tone: "danger" };
  }

  const forecast = resolvedRsvpForecast(appointment, pollRow);
  if (forecast === "coming") {
    return { kind: "confirmed_yes", label: "Приду", tone: "success" };
  }
  if (forecast === "not_coming") {
    return { kind: "confirmed_no", label: "Не приду", tone: "danger" };
  }

  return { kind: "scheduled", label: "По расписанию", tone: "neutral" };
}

export function findAttendanceOptions(pollRow) {
  const opts = pollRow?.poll?.options || pollRow?.options || [];
  const yes =
    opts.find((o) => o.forecast === "coming") ||
    opts.find((o) => optionTextLooksYes(o.text));
  const no =
    opts.find((o) => o.forecast === "not_coming") ||
    opts.find((o) => optionTextLooksNo(o.text));
  return { yes, no, all: opts };
}

export function hoursUntilSession(iso) {
  return (new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60);
}

export function shouldShowInlinePoll(appointment, pollRow) {
  if (
    appointment?.session_kind === "event" &&
    appointment?.event_kind === "competition"
  ) {
    return false;
  }
  if (!pollNeedsResponse(pollRow)) return false;
  const pollStart = pollRow.session?.start_datetime;
  if (!pollStart) return false;
  if (new Date(pollStart).toISOString() !== new Date(appointment.start_datetime).toISOString()) {
    return false;
  }
  const h = hoursUntilSession(appointment.start_datetime);
  return h <= 24 && h > 0;
}

export function countPendingPolls(polls) {
  return (polls || []).filter(pollNeedsResponse).length;
}

export function trainerName(row) {
  return row?.doctor?.full_name || row?.trainer?.full_name || "Тренер";
}

export function groupName(row, groups) {
  if (row?.session_kind === "event" && row?.event_kind === "competition") {
    return row?.title || "Соревнование";
  }
  return (
    row?.group?.name ||
    row?.service?.name ||
    row?.title ||
    groups?.find((g) => g.id === row?.group?.id)?.name ||
    groups?.[0]?.name ||
    "Занятие"
  );
}

/** Шаблонные цвета группы — при персонализации клуба используем --ca-accent. */
const LEGACY_DEFAULT_GROUP_COLORS = new Set([
  "#6d28d9",
  "#5b21b6",
  "#7c3aed",
  "#8b5cf6",
  "#4c1d95",
  "#a78bfa",
]);

function normalizeHexColor(hex) {
  const h = String(hex).trim().toLowerCase();
  if (/^#[0-9a-f]{3}$/.test(h)) {
    return `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
  }
  return h;
}

export function groupAccentColor(row, fallback = null) {
  const raw = row?.group?.calendar_color?.trim();
  if (raw && /^#[0-9a-fA-F]{3,8}$/.test(raw)) {
    if (!LEGACY_DEFAULT_GROUP_COLORS.has(normalizeHexColor(raw))) return raw;
  }
  return fallback;
}

/** Inline-стиль акцента слота; без своего цвета группы — CSS var(--ca-accent) из брендинга. */
export function sessionAccentStyle(row, cssVar = "--cal-accent") {
  const accent = groupAccentColor(row);
  return accent ? { [cssVar]: accent } : undefined;
}

export function sessionDurationMinutes(startIso, endIso) {
  if (!startIso || !endIso) return null;
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return Math.round(ms / 60000);
}

/** Нормализация строк ленты опросов API → формат dispatch для UI */
export function normalizeSurveyRows(rows) {
  return (rows || []).map((row) => ({
    id: row.dispatch_id || row.id,
    dispatch_id: row.dispatch_id || null,
    poll_id: row.poll_id,
    session_id: row.session_id,
    responded_at: row.responded_at,
    answers: row.answers,
    survey_type: row.survey_type || row.poll_type || "attendance",
    needs_response: row.needs_response,
    hours_until_session: row.hours_until_session,
    scheduled_send_at: row.scheduled_send_at,
    poll: {
      id: row.poll_id,
      title: row.title || row.poll?.title,
      poll_type: row.survey_type || row.poll_type || row.poll?.poll_type,
      description: row.description || row.poll?.description,
      questions: row.poll?.questions || [{ text: row.question || row.title }],
      options: row.poll?.options || row.options || [],
      closes_at: row.poll?.closes_at || null,
      status: row.poll?.status || "active",
    },
    session: row.session,
  }));
}

/** Маршрут и тело для POST /training-polls/:id/respond (в т.ч. pending без dispatch). */
export function buildPollRespondPayload(row, extra = {}) {
  const sid = row.session_id || row.session?.id;
  const body = { ...extra };
  if (row.poll_id) body.poll_id = row.poll_id;
  if (sid) body.session_id = sid;

  let routeId = row.dispatch_id || row.id;
  if (
    !row.dispatch_id &&
    row.poll_id &&
    (!routeId || String(routeId).startsWith("pending:"))
  ) {
    routeId = sid
      ? `pending:${row.poll_id}:${sid}`
      : `pending:${row.poll_id}`;
  }
  return { routeId, body };
}

/** Ссылка в календарь на день занятия с авто-открытием карточки. */
export function pollCalendarPath(row) {
  const sid = row?.session_id || row?.session?.id;
  const start = row?.session?.start_datetime;
  const params = new URLSearchParams();
  if (start) {
    const d = new Date(start);
    if (!Number.isNaN(d.getTime())) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      params.set("date", `${y}-${m}-${day}`);
    }
  }
  if (sid) params.set("session", String(sid));
  const qs = params.toString();
  return qs ? `/app/calendar?${qs}` : "/app/calendar";
}

export function notifyPollAnswered() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("baysport:poll-activity"));
  }
}

export function surveyTypeLabel(type) {
  if (type === "feedback") return "Обратная связь";
  if (type === "custom") return "Опрос";
  return "Подтверждение явки";
}

export function formatCountdownHours(hours) {
  if (hours == null || !Number.isFinite(hours)) return null;
  if (hours < 1) {
    const mins = Math.max(1, Math.round(hours * 60));
    return `через ${mins} мин`;
  }
  if (hours < 24) {
    const h = Math.round(hours * 10) / 10;
    return `через ${h} ч`;
  }
  const days = Math.round(hours / 24);
  return `через ${days} д`;
}
